import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { CheckCircle2, Copy, Loader2, Smartphone } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fcfa, PAYMENT_LABELS } from "@/lib/format";
import { useShop } from "@/lib/shop";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/motion";
import { QRCodeSVG } from "qrcode.react";

const OrderConfirmation = () => {
  useSeo({ title: "Confirmation de commande", noindex: true });
  const { orderNumber } = useParams();
  const location = useLocation();
  const shop = useShop();
  const [info, setInfo] = useState(location.state || null);
  const [confirming, setConfirming] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (!info) {
      try {
        const saved = JSON.parse(localStorage.getItem("sk_last_order"));
        if (saved && saved.orderNumber === orderNumber) setInfo(saved);
      } catch {}
    }
  }, [orderNumber, info]);

  const isWave = info?.method === "wave";
  const configured = isWave ? shop.wave_number : shop.orange_money_number;
  const payNumber = configured || shop.phone || "";
  const nationalNumber = payNumber.replace(/\D/g, "").replace(/^221/, "");
  const isTwoTimes = info?.paymentPlan === "two_times";
  const dueNow = isTwoTimes && info?.deposit ? info.deposit : info?.total;
  const amountInt = dueNow ? Math.trunc(dueNow) : 0;
  const waveDeepLink = `wave://send?phone=${nationalNumber}&amount=${amountInt}`;
  const omUssdLink = "tel:%23144%23";
  const omQrText = `Orange Money — Envoyez ${amountInt} FCFA au ${payNumber} — Ref ${orderNumber}`;

  const copyNumber = async () => {
    if (!payNumber) return;
    const text = payNumber.replace(/\s/g, "");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Numéro copié !");
    } catch {
      // Repli pour navigateurs sans permission clipboard (http, anciens mobiles)
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Numéro copié !");
      } catch {
        toast.error(`Copie impossible — notez le ${payNumber}`);
      }
    }
  };

  const confirmPayment = async () => {
    setConfirming(true);
    try {
      await api.post(`/orders/${info.orderId}/mobile-confirm`);
      setPaid(true);
      toast.success("Paiement signalé — vérification en cours");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <main data-testid="order-confirmation-page" className="">
      <div className="mx-auto max-w-3xl px-6 py-20 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Commande enregistrée</p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-bark sm:text-5xl">
            Jërëjëf, merci !
          </h1>
          <p className="mt-6 text-bark/70">
            Votre numéro de commande :
          </p>
          <p data-testid="order-number-display" className="mt-2 inline-block border border-terracotta bg-cream px-6 py-3 font-display text-3xl font-bold tracking-wide text-terracotta">
            {orderNumber}
          </p>
          <p className="mt-4 text-sm text-bark/50">
            Conservez ce numéro — il vous permettra de suivre votre commande.
          </p>
        </Reveal>

        {!paid && info && (
          <Reveal delay={0.15}>
            <div className="mt-12 border border-clay bg-cream p-8">
              <div className="flex items-center gap-4">
                <span
                  className="flex h-12 w-12 items-center justify-center rounded-full text-sand"
                  style={{ backgroundColor: isWave ? "#1DA1F2" : "#FF6600" }}
                >
                  <Smartphone className="h-6 w-6" />
                </span>
                <div>
                  <h2 className="font-display text-2xl font-semibold text-bark">
                    Payer {dueNow ? fcfa(dueNow) : ""} via {PAYMENT_LABELS[info.method]}
                  </h2>
                  {isTwoTimes && info?.remaining ? (
                    <p data-testid="two-times-note" className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-terracotta">
                      Acompte 50% maintenant — solde de {fcfa(info.remaining)} à la livraison
                    </p>
                  ) : (
                    <p className="text-xs uppercase tracking-[0.2em] text-bark/40">Virement manuel — vérifié à réception</p>
                  )}
                </div>
              </div>
              <ol className="mt-8 space-y-4 text-sm text-bark/70">
                <li className="flex gap-3">
                  <span className="font-bold text-terracotta">1.</span>
                  Ouvrez votre application {PAYMENT_LABELS[info.method]} et choisissez « Envoyer de l'argent ».
                </li>
                <li className="flex items-center gap-3">
                  <span className="font-bold text-terracotta">2.</span>
                  <span>
                    Envoyez {dueNow ? fcfa(dueNow) : "le montant"} au{" "}
                    {payNumber ? (
                      <button data-testid="copy-payment-number" onClick={copyNumber} className="inline-flex items-center gap-1 font-bold text-terracotta underline-offset-2 hover:underline">
                        {payNumber} <Copy className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <span data-testid="payment-number-missing" className="font-semibold text-bark">numéro communiqué par la boutique</span>
                    )}{" "}
                    (Sama-Keur).
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-terracotta">3.</span>
                  Indiquez votre numéro de commande <strong>{orderNumber}</strong> en référence, puis confirmez ci-dessous.
                </li>
              </ol>

              {payNumber && (
                <button
                  type="button"
                  data-testid="copy-number-big-button"
                  onClick={copyNumber}
                  className="mt-6 flex h-14 w-full items-center justify-center gap-3 rounded-full border-2 border-terracotta bg-cream text-sm font-bold uppercase tracking-[0.15em] text-terracotta transition-colors duration-300 hover:bg-terracotta hover:text-sand"
                >
                  <Copy className="h-5 w-5" />
                  Copier le numéro — {payNumber}
                </button>
              )}

              {isWave && nationalNumber && amountInt > 0 && (
                <div className="mt-6">
                  <a
                    href={waveDeepLink}
                    data-testid="pay-with-wave-button"
                    className="flex h-16 w-full items-center justify-center gap-3 rounded-full bg-[#0A7CA8] text-base font-bold uppercase tracking-[0.15em] text-white transition-opacity duration-300 hover:opacity-90"
                  >
                    Ouvrir Wave — {fcfa(dueNow)}
                  </a>
                  <p className="mt-3 text-center text-xs text-bark/50">
                    Sur téléphone, Wave s'ouvre avec le montant et le numéro déjà remplis. Si rien ne se passe, utilisez le
                    bouton « Copier le numéro » ci-dessus et envoyez le montant manuellement.
                  </p>
                </div>
              )}

              {!isWave && nationalNumber && amountInt > 0 && (
                <div className="mt-6 space-y-4">
                  <a
                    href={omUssdLink}
                    data-testid="pay-with-om-button"
                    className="flex h-14 w-full items-center justify-center gap-3 rounded-full bg-[#C74A00] text-sm font-bold uppercase tracking-[0.15em] text-white transition-opacity duration-300 hover:opacity-90"
                  >
                    Ouvrir Orange Money (#144#)
                  </a>
                  <div className="hidden flex-col items-center gap-3 border border-clay bg-white p-6 md:flex" data-testid="om-qr-block">
                    <QRCodeSVG value={omQrText} size={180} level="M" includeMargin data-testid="om-qr-code" />
                    <p className="text-center text-xs text-bark/50">
                      Scannez avec votre téléphone pour afficher le numéro, le montant et la référence de votre commande.
                    </p>
                  </div>
                </div>
              )}

              <button
                data-testid="mobile-payment-confirm-button"
                onClick={confirmPayment}
                disabled={confirming}
                className="mt-8 flex h-14 w-full items-center justify-center gap-3 rounded-full bg-terracotta text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-terracotta-dark disabled:opacity-50"
              >
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                J'ai effectué le paiement
              </button>
            </div>
          </Reveal>
        )}

        {paid && (
          <Reveal>
            <div data-testid="mobile-payment-success" className="mt-12 flex flex-col items-center border border-terracotta/30 bg-cream p-10 text-center">
              <CheckCircle2 className="h-14 w-14 text-terracotta" />
              <h2 className="mt-4 font-display text-3xl font-semibold text-bark">Paiement signalé</h2>
              <p className="mt-2 max-w-md text-sm text-bark/60">
                Merci ! Nous vérifions la réception de votre virement sur notre compte. Votre commande {orderNumber} sera
                confirmée dès vérification — notre équipe vous contacte très vite pour organiser la livraison.
              </p>
              <Link
                to="/suivi"
                data-testid="go-to-tracking-button"
                className="mt-8 flex h-14 items-center rounded-full bg-terracotta px-8 text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark"
              >
                Suivre ma commande
              </Link>
            </div>
          </Reveal>
        )}

        <div className="mt-12 text-center">
          <Link to="/boutique" data-testid="confirmation-back-to-shop" className="text-xs font-bold uppercase tracking-[0.2em] text-bark/50 transition-colors hover:text-terracotta">
            Retour à la boutique
          </Link>
        </div>
      </div>
    </main>
  );
};

export default OrderConfirmation;
